/** AI client: /api/v1/ai/* (QXM PRD §16 AI-01…05, Q11). Everything is hidden unless GET /ai/status says enabled. */
import { useAuth } from '../../../lib/auth';
import { useGet } from '../records/kit';

export type Verdict = 'COVERED' | 'PARTIAL' | 'MISSING';
export type RunStatus = 'QUEUED' | 'RUNNING' | 'DONE' | 'FAILED';

export interface AiStatus {
  enabled: boolean;
  reason: 'PROVIDER_OFF' | 'NO_KEY' | 'ORG_OFF' | 'CAPABILITY_OFF' | 'BUDGET_REACHED' | null;
  ask: boolean;
  gapCheck: boolean;
  budget: { usedTokens: number; monthlyTokens: number } | null;
  allowConfidential: boolean;
  conversationDays: number;
}

export interface Citation { docNumber: string; rev: string; page: number; documentId: string; pageNo: number }
export interface AskAnswer { conversationId: string; answer: string; citations: Citation[]; unknown: boolean; sent: boolean; disclaimer: string }
export interface AiMessage { role: 'user' | 'assistant'; text: string; at: string; citations?: Citation[]; unknown?: boolean }
export interface Conversation { id: string; title: string; updatedAt: string; expiresAt: string; messages?: AiMessage[] }

export interface Estimate {
  clauses: number; carried: number; groups: number; passages: number; documents: number; inputTokens: number; outputTokens: number; totalTokens: number;
  budget: { usedTokens: number; monthlyTokens: number; leftTokens: number }; withinBudget: boolean;
}
export interface GapEvidence { documentId: string; docNumber: string | null; title: string; rev: string | null; page: number; quote: string | null }
export interface GapItem {
  clauseId: string; number: string; title: string; verdict: Verdict; severity: 'MAJOR' | 'MINOR'; evidence: GapEvidence[]; missing: string;
  humanState: 'COVERED' | 'PARTIAL' | 'NONE'; carried?: boolean; hiddenEvidence?: number;
}
export interface GapRun {
  id: string; standardId: string; status: RunStatus; readiness: number | null; humanReadiness: number | null; label: string; model: string;
  inputTokens: number; outputTokens: number; costUsd: number; error: string | null; createdBy: string | null; createdAt: string; finishedAt: string | null;
  estimate: Partial<Estimate>; scope: { includeUncontrolled?: boolean; sinceRunId?: string | null };
  items?: GapItem[]; disclaimer?: string;
}

/** GET /ai/status for people in an organisation (platform admins without a support session: nothing). */
export function useAiStatus() {
  const { user } = useAuth();
  return useGet<AiStatus>(['ai', 'status'], user?.org ? '/ai/status' : null);
}

export const VERDICT_LABEL: Record<Verdict, string> = { COVERED: 'Covered', PARTIAL: 'Partial', MISSING: 'Missing' };
export const VERDICT_TONE: Record<Verdict, 'ok' | 'warn' | 'danger'> = { COVERED: 'ok', PARTIAL: 'warn', MISSING: 'danger' };
export const HUMAN_LABEL: Record<GapItem['humanState'], string> = { COVERED: 'Covered', PARTIAL: 'Partial', NONE: 'None' };

/** Pure: the viewer link of a citation (opens the document at the page). */
export const citationHref = (c: Pick<Citation, 'documentId' | 'pageNo'>) => `/documents/${c.documentId}?page=${c.pageNo}`;

/** Pure: "[QP-PUR-001 Rev B, p. 3]" markers in an answer → text and citation parts, for rendering links. */
export function splitAnswer(text: string, citations: Citation[]): ({ text: string } | { cite: Citation; label: string })[] {
  const out: ({ text: string } | { cite: Citation; label: string })[] = [];
  const re = /\[([A-Za-z0-9][A-Za-z0-9._/-]*) Rev ([A-Za-z0-9._-]+), p\. ?(\d+)\]/g;
  let last = 0;
  for (const m of text.matchAll(re)) {
    const c = citations.find((x) => x.docNumber.toUpperCase() === m[1].toUpperCase() && x.rev.toUpperCase() === m[2].toUpperCase() && x.page === Number(m[3]));
    if (m.index! > last) out.push({ text: text.slice(last, m.index) });
    out.push(c ? { cite: c, label: `${c.docNumber} Rev ${c.rev}, p. ${c.page}` } : { text: m[0] });
    last = m.index! + m[0].length;
  }
  if (last < text.length) out.push({ text: text.slice(last) });
  return out;
}

/** Pure: "12,345 of 300,000 tokens (4%)". */
export function budgetLine(b: { usedTokens: number; monthlyTokens: number } | null | undefined): string {
  if (!b) return '';
  const pct = b.monthlyTokens ? Math.round((b.usedTokens / b.monthlyTokens) * 100) : 100;
  return `${b.usedTokens.toLocaleString('en-PH')} of ${b.monthlyTokens.toLocaleString('en-PH')} tokens this month (${pct}%)`;
}

export const OFF_REASON: Record<NonNullable<AiStatus['reason']>, string> = {
  PROVIDER_OFF: 'ITCSI has not switched on an AI provider for this server.',
  NO_KEY: 'ITCSI has not added the AI provider key yet.',
  ORG_OFF: 'The AI assistant is off for your organisation.',
  CAPABILITY_OFF: 'This AI feature is off for your organisation.',
  BUDGET_REACHED: "This month's AI budget is used up.",
};
